import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';
import { CheckboxAPI, FetchAPI, FilterAPI, InputAPI, TabControlAPI, TabWithContentAPI, Tooltip } from '@philips-internal/oswald-ui-components';
import Controller, { OnDynamicContentRender, OnMount, OnNavigate, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { NavigateEvent, RenderEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { ReactComponent as AttachSVG } from '../../../resources/assets/commons/attach_icon.svg';
import { ReactComponent as OpenSVG } from '../../../resources/assets/commons/open.svg';
import { FragmentNames, MessageListAliases } from '../../codegen';
import { MessageRoutesApplication } from '../../constants';
import { MessageCount, MessageVO } from '../../interfaces';
import { MessagesService } from '../../services';
import BaseController from '../BaseController';

@Controller({ fragment: FragmentNames.MESSAGE_LIST })
export default class MessageListController extends BaseController {

  @Inject(MessageListAliases.TAB_CONTROL_MESSAGE)
  tabControlMessage!: TabControlAPI;

  @Inject(MessageListAliases.TAB_MESSAGE_READ)
  tabMessageRead!: TabWithContentAPI;

  @Inject(MessageListAliases.TAB_MESSAGE_NOT_READ)
  tabMessageNotRead!: TabWithContentAPI;

  @Inject(MessageListAliases.FETCH_MESSAGE_READ)
  fetchMessageRead!: FetchAPI;

  @Inject(MessageListAliases.FETCH_MESSAGE_NOT_READ)
  fetchMessageNotRead!: FetchAPI;

  @Inject(MessageListAliases.FILTER)
  filterMessage!: FilterAPI;

  @Inject(MessageListAliases.INPUT_SUBJECT)
  inputSubject!: InputAPI;

  @Inject(MessageListAliases.INPUT_DESCRIPTION)
  inputDescription!: InputAPI;

  @Inject(MessageListAliases.CHECKBOX_PRIORITY)
  checkboxPriority!: CheckboxAPI;

  messageCount!: MessageCount;

  async setBadgeTabs(): Promise<void> {
    try {
      this.messageCount = await MessagesService.count();
      this.tabMessageRead.setBadge(this.messageCount.countRead || 0);
      this.tabMessageNotRead.setBadge(this.messageCount.countNotRead || 0);
    } catch (e) {
      console.log(e);
    }
  }

  @OnMount({ target: MessageListAliases.LAYOUT_MESSAGE })
  onMount(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: 'messages' } });
    dispatchEvent(event);
    void this.setBadgeTabs();
  }

  @OnMount({ target: [MessageListAliases.TAB_MESSAGE_READ, MessageListAliases.TAB_MESSAGE_NOT_READ] })
  onMountTabsContent(): void {
    void this.setBadgeTabs();
  }

  @OnMount(MessageListAliases.TAB_CONTROL_MESSAGE)
  onMountTabControlMessage(): void {
    this.tabControlMessage.setActiveTab(this.tabControlMessage.getDefaultTab());
  }

  @OnNavigate({ target: MessageListAliases.TAB_CONTROL_MESSAGE })
  onNavigateTabControlMessage(event: NavigateEvent<string>): void {
    if (!event.to) {
      return;
    }
    void this.setBadgeTabs();
    this.filterMessage.resetDefaultData();
  }

  @OnDynamicContentRender({ target: [MessageListAliases.TABLE_MESSAGE_NOT_READ, MessageListAliases.TABLE_MESSAGE_READ] })
  onRenderColumns(event: RenderEvent): void {
    switch (event.entry) {
      case 'actions':
        event.render((data: MessageVO): ReactElement => {
          const clickStyle = {
            cursor: 'pointer',
          };
          const clickSpaceStyle = {
            ...clickStyle,
            marginLeft: '4px',
          };
          const actionsStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          };
          return (<div style={actionsStyle}>
            <Tooltip value="open.action">
              <div style={clickSpaceStyle}>
                <OpenSVG onClick={() => this.openMessage(data.message.id)}/>
              </div>
            </Tooltip>
          </div>);
        });
        break;

      case 'subject':
        event.render((data: MessageVO): ReactElement => {
          const actionsStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          };
          return (
            <div style={actionsStyle}>
              {this.renderIconAttach(data.attach)}
              <Tooltip value={data.subject} multiline={true}>
                <div>
                  {data.subject}
                </div>
              </Tooltip>
            </div>);
        });
        break;
    }
  }

  @OnSubmit(MessageListAliases.FILTER)
  onSubmitFilterMessage(event: SubmitEvent): void {
    const params: any = {
      dashboard: false,
    };
    if (event && event.record['messageSubject']) {
      params.messageSubject = this.inputSubject.getValue();
    }
    if (event && event.record['messageDescription']) {
      params.messageDescription = this.inputDescription.getValue();
    }
    if (event && event.record['messagePriority']) {
      params.urgent = this.checkboxPriority.getValue();
    }
    if (this.tabControlMessage.getActiveTab() === MessageListAliases.TAB_MESSAGE_READ) {
      this.fetchMessageRead.setParameters(params);
    }
    if (this.tabControlMessage.getActiveTab() === MessageListAliases.TAB_MESSAGE_NOT_READ) {
      this.fetchMessageNotRead.setParameters(params);
    }
  }

  renderIconAttach(attach: boolean): ReactElement {
    if (attach) {
      return (
        <Tooltip value="message-card.attach">
          <div>
            <AttachSVG fill="#1477a9"/>
          </div>
        </Tooltip>
      );
    }
    return (<div/>);
  }

  openMessage(id: number): void {
    applicationContext.redirect(MessageRoutesApplication.MESSAGE_ITEM, { id });
  }

}
